/**
 * Auth Context for Client Portal
 *
 * Provides authentication state and functions throughout the app
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
    type ClientUser,
    signInClient,
    signOutClient,
    getClientProfile,
    onAuthChange
} from '../lib/auth';

interface AuthContextType {
    firebaseUser: FirebaseUser | null;
    clientUser: ClientUser | null;
    loading: boolean;
    error: string | null;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [clientUser, setClientUser] = useState<ClientUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Listen to Firebase auth state changes
    useEffect(() => {
        const unsubscribe = onAuthChange(async (user) => {
            setFirebaseUser(user);

            if (user) {
                // Fetch client profile
                const profile = await getClientProfile(user.uid);
                setClientUser(profile);
            } else {
                setClientUser(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signIn = async (email: string, password: string) => {
        setLoading(true);
        setError(null);
        try {
            const profile = await signInClient(email, password);
            setClientUser(profile);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Login gagal';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const signOut = async () => {
        setLoading(true);
        try {
            await signOutClient();
            setClientUser(null);
        } catch (err) {
            console.error('Sign out error:', err);
        } finally {
            setLoading(false);
        }
    };

    const clearError = () => setError(null);

    const value: AuthContextType = {
        firebaseUser,
        clientUser,
        loading,
        error,
        signIn,
        signOut,
        clearError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
